/**
 * 仓内现状：⓿~④ + 异常包裹 六张卡片，移植产品原型 page.tsx 的卡片区。
 *
 * - 点卡片：下方流向表按该环节的包裹数从高到低排序（再点一次取消）
 * - 点包裹数 → 包裹明细；点包牌数（②~④）→ 包牌明细
 * - 告警行按「流向」（发干线目的地 + 发支线 WHS）计数，口径见 lib/tree.ts 的 stageAlerts
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { ALL_STAGES, C, PILE_BAGS, isBagStage } from '../constants';
import type { BagRecord, DetailStage, StageKey } from '../types';
import type { StageAlert, StageCell } from '../lib/tree';
import { cellDwellHours } from '../lib/tree';
import { useFmt } from '../lib/useFmt';
import { I18N, useLabels } from '../lib/useLabels';
import HoverCard from './HoverCard';

export const LINK_CLS = 'cursor-pointer underline decoration-dotted underline-offset-2 hover:text-[#722ED1]';

interface Props {
    totals: Record<StageKey, StageCell>;
    alerts: Record<StageKey, StageAlert>;
    now: number;
    overdueThresholdHours: number;
    focusStage: StageKey | 0;
    onCardClick: (s: StageKey) => void;
    onOpenParcels: (stage: DetailStage, total: number) => void;
    onOpenBags: (stage: StageKey, bags: BagRecord[]) => void;
}

const StageCards: React.FC<Props> = ({
    totals,
    alerts,
    now,
    overdueThresholdHours: th,
    focusStage,
    onCardClick,
    onOpenParcels,
    onOpenBags
}) => {
    const { t } = useTranslation();
    const fmt = useFmt();
    const L = useLabels();

    // 卡片上的红 / 橙色提示条；openStage 不为空时可点开对应的包裹明细
    const alertLine = (
        key: string,
        tone: 'red' | 'orange',
        label: string,
        big: string,
        rest: string,
        card: React.ReactNode,
        openStage?: DetailStage,
        openTotal?: number
    ) => {
        const color = tone === 'red' ? C.danger : C.warn;
        return (
            <HoverCard key={key} card={card} accent={color} style={{ display: 'block' }}>
                <div
                    onClick={(e) => {
                        if (openStage === undefined) return;
                        e.stopPropagation();
                        onOpenParcels(openStage, openTotal ?? 0);
                    }}
                    className={`mt-1.5 rounded text-[12.5px] ${openStage !== undefined ? 'cursor-pointer' : ''}`}
                    style={{ padding: '3px 6px', background: tone === 'red' ? C.dangerBg : C.warnBg, color: C.text2 }}
                >
                    <span style={{ color, fontSize: 10, marginRight: 4 }}>▲</span>
                    {label}
                    <span className="mx-1 text-[16px] font-bold" style={{ color, letterSpacing: '-.3px' }}>
                        {big}
                    </span>
                    {rest}
                </div>
            </HoverCard>
        );
    };

    const cardTitle = (text: string) => <div style={{ fontWeight: 600, color: C.text1 }}>{text}</div>;
    const actionLine = (text: string) => (
        <div>
            <b>{t(`${I18N}.alerts.suggestedAction`)}</b> · {text}
        </div>
    );

    return (
        <div className="grid grid-cols-2 gap-3 p-3 md:grid-cols-3 xl:grid-cols-6">
            {ALL_STAGES.map((s) => {
                const c = totals[s];
                const a = alerts[s];
                const active = focusStage === s;
                const bagStage = isBagStage(s);
                const title = L.stageTitle(s);
                const numCls = `whitespace-nowrap ${bagStage ? 'text-[22px]' : 'text-[26px]'} font-bold leading-tight`;
                return (
                    <div
                        key={s}
                        onClick={() => onCardClick(s)}
                        className="cursor-pointer rounded-md p-3"
                        style={{ border: `1px solid ${C.border}`, background: active ? C.primaryBg : '#fff' }}
                    >
                        <div className="text-[12.5px]" style={{ color: C.text3 }}>
                            {title}
                        </div>
                        <div className="mt-0.5 flex items-baseline gap-2">
                            <div>
                                <div
                                    className={`${numCls} ${LINK_CLS}`}
                                    style={{ color: C.text1 }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onOpenParcels(s, c.parcels);
                                    }}
                                >
                                    {fmt.pcs(c.parcels)}
                                </div>
                                <div className="whitespace-nowrap text-[12.5px]" style={{ color: C.text3 }}>
                                    {t(`${I18N}.current.parcels`)}
                                </div>
                            </div>
                            {(s === 0 || bagStage) && (
                                <div className="min-w-0 pl-2" style={{ borderLeft: `1px solid ${C.border}` }}>
                                    <div
                                        className={`${numCls} ${bagStage ? LINK_CLS : ''}`}
                                        style={{ color: C.text1 }}
                                        onClick={(e) => {
                                            if (!bagStage) return;
                                            e.stopPropagation();
                                            onOpenBags(s, c.bags);
                                        }}
                                    >
                                        {s === 0 ? fmt.num(c.bigBags) : fmt.num(c.bags.length)}
                                    </div>
                                    <div className="whitespace-nowrap text-[12.5px]" style={{ color: C.text3 }}>
                                        {s === 0 ? t(`${I18N}.current.bigBags`) : t(`${I18N}.current.bags`)}
                                    </div>
                                </div>
                            )}
                            {bagStage && (
                                <div className="min-w-0 pl-2" style={{ borderLeft: `1px solid ${C.border}` }}>
                                    <div className="whitespace-nowrap text-[22px] font-bold leading-tight" style={{ color: C.text1 }}>
                                        0
                                    </div>
                                    <div className="whitespace-nowrap text-[12.5px]" style={{ color: C.text3 }}>
                                        {t(`${I18N}.current.batches`)}
                                    </div>
                                </div>
                            )}
                        </div>
                        {a.late > 0 &&
                            alertLine(
                                'late',
                                'red',
                                t(`${I18N}.alerts.lateLabel`, { th }) ?? '',
                                String(a.late),
                                t(`${I18N}.alerts.lateRest`, { dwell: fmt.dwell(a.lateMax) }) ?? '',
                                <>
                                    {cardTitle(t(`${I18N}.alerts.lateTitle`, { stage: title, th }) ?? '')}
                                    <div>
                                        {t(bagStage ? `${I18N}.alerts.lateBodyBag` : `${I18N}.alerts.lateBodyParcel`, {
                                            n: a.late,
                                            th,
                                            dwell: fmt.dwell(a.lateMax)
                                        })}
                                    </div>
                                    {actionLine(L.action(s))}
                                </>
                            )}
                        {s === 1 &&
                            a.err > 0 &&
                            alertLine(
                                'err',
                                'red',
                                t(`${I18N}.alerts.errLabel`) ?? '',
                                fmt.num(a.err),
                                t(`${I18N}.alerts.errRest`, { flows: a.errFlows }) ?? '',
                                <>
                                    {cardTitle(t(`${I18N}.alerts.errTitle`, { n: fmt.num(a.err) }) ?? '')}
                                    <div>{t(`${I18N}.alerts.errBody`)}</div>
                                    {actionLine(t(`${I18N}.alerts.errAction`) ?? '')}
                                </>,
                                5,
                                a.err
                            )}
                        {s === 2 &&
                            a.strag > 0 &&
                            alertLine(
                                'strag',
                                'red',
                                t(`${I18N}.alerts.stragLabel`) ?? '',
                                fmt.num(a.strag),
                                t(`${I18N}.alerts.stragRest`, { flows: a.stragFlows }) ?? '',
                                <>
                                    {cardTitle(t(`${I18N}.alerts.stragTitle`, { n: fmt.num(a.strag) }) ?? '')}
                                    <div>{t(`${I18N}.alerts.stragBody`)}</div>
                                    {actionLine(t(`${I18N}.alerts.stragAction`) ?? '')}
                                </>
                            )}
                        {bagStage &&
                            a.pile > 0 &&
                            alertLine(
                                'pile',
                                'orange',
                                t(`${I18N}.alerts.pileLabel`) ?? '',
                                String(a.pile),
                                t(`${I18N}.alerts.pileRest`, { bags: fmt.num(a.pileBags) }) ?? '',
                                <>
                                    {cardTitle(t(`${I18N}.alerts.pileTitle`) ?? '')}
                                    <div>
                                        {t(`${I18N}.alerts.pileBody`, { n: a.pile, min: PILE_BAGS, bags: fmt.num(a.pileBags) })}
                                    </div>
                                    {actionLine(L.action(s))}
                                </>
                            )}
                        <div className="mt-1.5 text-[12px]" style={{ color: C.text3 }}>
                            {t(`${I18N}.current.maxDwell`, { dwell: fmt.dwell(cellDwellHours(c, now)) })}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default StageCards;
